import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { AuthSession, AuthUser } from '../services/authService'
import { getAuthService } from '../services/authService'
import { readMe } from '../services/adminService'

interface AuthValue {
  /** null once loading has finished and nobody is signed in */
  session: AuthSession | null
  user: AuthUser | null
  /** True until the stored session has been checked */
  loading: boolean
  /**
   * The server has said whether this person is an administrator (/api/me,
   * library_admins). Until then `user.role` is the list built into the page.
   */
  roleKnown: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

/**
 * Only the standalone website mounts this. The Miro panel has its own entry
 * point and never loads it — people using the panel are already signed in to
 * the Miro team.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useMemo(() => getAuthService(), [])
  const [session, setSession] = useState<AuthSession | null>(null)
  const [loading, setLoading] = useState(true)
  /*
   * WHO IS AN ADMINISTRATOR, from the server (Huanrui, 2026-10-09): the
   * Administrator skill can make anybody one, so a list compiled into the
   * page can no longer be the answer. Asked once per signed-in address.
   */
  const [admin, setAdmin] = useState<{ email: string; admin: boolean } | null>(null)
  const email = session?.user.email ?? ''
  useEffect(() => {
    if (!email) return
    let cancelled = false
    readMe()
      .then((me) => {
        if (!cancelled) setAdmin({ email, admin: me.admin })
      })
      .catch(() => {
        // Not answered: the built-in list stands for this visit.
        if (!cancelled) setAdmin({ email, admin: session?.user.role === 'admin' })
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  useEffect(() => {
    const subscription = auth.onAuthStateChange((_event, next) => {
      setSession(next)
      setLoading(false)
    })
    return () => subscription.unsubscribe()
  }, [auth])

  const value = useMemo<AuthValue>(() => {
    const known = Boolean(admin && admin.email === email)
    const user = session?.user
      ? known
        ? { ...session.user, role: admin!.admin ? ('admin' as const) : ('designer' as const) }
        : session.user
      : null
    return {
      session,
      user,
      loading,
      roleKnown: !session || known,
      signOut: () => auth.signOut(),
    }
  }, [session, loading, auth, admin, email])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside an <AuthProvider>')
  return context
}
